/**
 * client/src/structure/ChapterList.tsx — the chapters, and the four things a human does to them.
 *
 * §3.4 describes this screen as one scrolling column, one card per chapter, carrying the role, the
 * word count, **the first 140 characters of the text** (reading the openings is the fastest way a
 * human fixes a mis-detected start) and a thick draggable rail at the detected boundary. This is that
 * column, with the verbs attached.
 *
 * ── The one distinction the whole file is arranged around ───────────────────────────────────────
 *
 * Two of the four verbs happen **immediately** and two are **staged**, and the line between them is
 * not how big they are — it is whether they move a `sid`:
 *
 *   · role, narrate-or-not, rename, "start the book here"  → a column write on this edition. Instant.
 *   · merge, split                                          → a NEW EDITION and a `doc_remaps`
 *                                                             migration, because they renumber every
 *                                                             chapter after them and therefore every
 *                                                             sentence id in the rest of the book.
 *
 * So merges and splits accumulate in a list the human can see and cancel, are priced by the server
 * before anything is written ("14 chapters change · 0 GPU · re-stitch 41 pages"), and are applied
 * together in one transaction. A merge that fired the instant it was tapped would renumber a book
 * five times to fix five chapters, and each renumber expires every page artifact in it.
 *
 * ⚠️ Mobile first, at 390 px. Every control here is a real button with a 44 px tap target rather than
 * a hover affordance, and the cards stack. This screen is used one-handed, in bed, on the phone that
 * just read the wrong chapter aloud.
 *
 * ⚠️ **Recorded deviation: the rail is rendered and tapped, not dragged.** §3.4 asks for "a thick
 * draggable THE BOOK STARTS HERE rail at the detected boundary". The rail is here and it is thick,
 * and moving it is a button on the chapter you want (`Start the book here`) rather than a drag.
 * Dragging a boundary through a 72-chapter list on a 390 px screen means an auto-scrolling drag
 * target under a thumb that is also the scroll gesture — it is the control that is hardest to hit and
 * easiest to miss by one, on the screen whose entire job is being exactly right about one boundary.
 * The same write, `PATCH {bodyStartSid}`, is what the reader's sentence menu calls.
 */

import { useState } from "react";

import type { StructureChapter, StructureView } from "../lib/api.ts";
import type { ChapterRole } from "../../../shared/doc.ts";
import { IconCheck } from "../App.tsx";

/** A chapter row as the P3 route sends it — `StructureChapter` plus what an editor needs. */
export interface EditableChapter extends StructureChapter {
  blockCount: number;
  firstBlockId: string | null;
  /** Where "start the book here" lands. Null for a chapter with no prose in it at all. */
  firstSid: string | null;
  openingBlocks: { id: string; kind: string; text: string }[];
}

/** What this route noticed about the book, offered rather than applied. See `suggestionsFor`. */
export interface StructureSuggestion {
  id: string;
  title: string;
  detail: string;
  ops: RestructureOp[];
  chaptersAfter: number;
}

export type RestructureOp =
  | { op: "merge"; chapterId: string }
  | { op: "split"; blockId: string; title?: string };

/** `StructureView` as the P3 route actually answers it. */
export interface EditableStructure extends Omit<StructureView, "chapters"> {
  chapters: EditableChapter[];
  suggestions: StructureSuggestion[];
  blockMode: boolean;
  pageCount: number;
  narratedPages: number;
  charCount: number;
  sentenceCount: number;
}

const ROLE_LABEL: Record<ChapterRole, string> = { front: "Front", body: "Body", back: "Back" };
const ROLES: ChapterRole[] = ["front", "body", "back"];

export interface ChapterListProps {
  view: EditableStructure;
  ops: RestructureOp[];
  onStage: (ops: RestructureOp[]) => void;
  onUnstage: (predicate: (op: RestructureOp) => boolean) => void;
  /** An immediate write against this edition. Resolves when the screen has been reloaded. */
  onPatch: (patch: Record<string, unknown>) => Promise<void>;
  busy: boolean;
}

export function ChapterList({ view, ops, onStage, onUnstage, onPatch, busy }: ChapterListProps) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const opening = view.openingChapterOrds ?? [];

  const mergedAway = new Set(ops.flatMap((o) => (o.op === "merge" ? [o.chapterId] : [])));
  const splitBlocks = new Map(ops.flatMap((o) => (o.op === "split" ? [[o.blockId, o.title ?? ""] as const] : [])));

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">The chapters, in reading order</h2>
      <p className="ab-hint">
        Everything before the rail is kept and readable — it just isn&rsquo;t read aloud. Nothing on
        this screen deletes a word of the book.
      </p>
      <p className={opening.length === 0 ? "ab-note ab-note-warn" : "ab-note"}>
        {opening.length === 0
          ? "Choose the real first one or two chapters below. Library narration is held until you do, so a Part heading or dedication is never guessed as chapter one."
          : `${opening.length === 1 ? "One opening chapter" : "Two opening chapters"} selected. These finish across the library before whole-book narration continues.`}
      </p>

      <ol className="ab-chapters">
        {view.chapters.map((c, i) => {
          const previous = view.chapters[i - 1];
          const merging = mergedAway.has(c.id);
          // A heading sitting one or two blocks into the chapter is the shape P1 found in Pride and
          // Prejudice: Gutenberg leaves an illustration plate's caption in front of "CHAPTER II.", so
          // the chapter both starts too early and takes its title from the caption.
          const strandedHeading =
            c.openingBlocks.length > 0 && c.openingBlocks[0]!.kind !== "heading"
              ? c.openingBlocks.slice(1).find((b) => b.kind === "heading" && b.text.trim() !== "")
              : undefined;
          const splitStaged = c.openingBlocks.some((b) => splitBlocks.has(b.id));
          const openingIndex = opening.indexOf(c.ord);
          const openingEligible = c.role === "body" && c.speak;

          return (
            <li key={c.id}>
              {c.isBodyStart && (
                <div className="ab-rail" aria-label="The book starts here">
                  <span>THE BOOK STARTS HERE</span>
                </div>
              )}

              <article className={c.role === "body" ? "ab-chapter is-body" : "ab-chapter"}>
                <header>
                  {renaming === c.id ? (
                    <Rename
                      initial={c.title}
                      busy={busy}
                      onCancel={() => setRenaming(null)}
                      onSave={async (title) => {
                        await onPatch({ chapterId: c.id, title });
                        setRenaming(null);
                      }}
                    />
                  ) : (
                    <>
                      <h3>{c.title}</h3>
                      <span className="ab-chapter-count">
                        {c.charCount.toLocaleString()} chars · {c.blockCount} blocks
                      </span>
                    </>
                  )}
                </header>

                <p className="ab-chapter-preview">{c.preview}</p>

                {merging ? (
                  <div className="ab-actions">
                    <span className="ab-chip ab-chip-work">
                      <IconCheck /> Will merge into &ldquo;{previous?.title ?? "the chapter above"}&rdquo;
                    </span>
                    <button
                      type="button"
                      className="ab-btn ab-btn-ghost ab-btn-sm"
                      onClick={() => onUnstage((o) => o.op === "merge" && o.chapterId === c.id)}
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="ab-actions">
                      {/* The role decides what is narrated at all — front and back matter are kept,
                          readable and searchable, and never spoken (§3.5). */}
                      <div className="ab-segment" role="group" aria-label={`Role of ${c.title}`}>
                        {ROLES.map((role) => (
                          <button
                            key={role}
                            type="button"
                            className={role === c.role ? "ab-segment-item is-on" : "ab-segment-item"}
                            disabled={busy || role === c.role}
                            onClick={() => void onPatch({ chapterId: c.id, role })}
                          >
                            {ROLE_LABEL[role]}
                          </button>
                        ))}
                      </div>

                      <label className="ab-check">
                        <input
                          type="checkbox"
                          checked={c.speak}
                          disabled={busy}
                          onChange={(e) => void onPatch({ chapterId: c.id, speak: e.target.checked })}
                        />
                        {/* Karti called dedications "often lovely" — §3.5 gives them an explicit
                            one-tap toggle rather than a policy. This is it, for any chapter. */}
                        <span>Read this one aloud</span>
                      </label>
                    </div>

                    <div className="ab-actions">
                      {openingIndex >= 0 ? (
                        <>
                          <span className="ab-chip ab-chip-work">Opening {openingIndex + 1}</span>
                          <button
                            type="button"
                            className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                            disabled={busy}
                            onClick={() =>
                              void onPatch({ openingChapterOrds: opening.filter((ord) => ord !== c.ord) })
                            }
                          >
                            Remove from opening
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                          disabled={busy || !openingEligible || opening.length >= 2}
                          title={
                            !openingEligible
                              ? "Opening chapters must be Body chapters marked Read this one aloud."
                              : opening.length >= 2
                                ? "Remove one of the two selected chapters first."
                                : undefined
                          }
                          onClick={() => {
                            const selected = new Set([...opening, c.ord]);
                            const ordered = view.chapters.filter((chapter) => selected.has(chapter.ord)).map((chapter) => chapter.ord);
                            void onPatch({ openingChapterOrds: ordered });
                          }}
                        >
                          Add to opening narration
                        </button>
                      )}
                      {!c.isBodyStart && c.firstSid !== null && (
                        <button
                          type="button"
                          className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                          disabled={busy}
                          onClick={() => void onPatch({ bodyStartSid: c.firstSid })}
                        >
                          Start the book here
                        </button>
                      )}
                      <button
                        type="button"
                        className="ab-btn ab-btn-ghost ab-btn-sm"
                        disabled={busy}
                        onClick={() => setRenaming(c.id)}
                      >
                        Rename
                      </button>
                      {i > 0 && (
                        <button
                          type="button"
                          className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                          onClick={() => onStage([{ op: "merge", chapterId: c.id }])}
                        >
                          Merge into &ldquo;{shorten(previous?.title ?? "")}&rdquo;
                        </button>
                      )}
                      {strandedHeading !== undefined && !splitStaged && i > 0 && (
                        <button
                          type="button"
                          className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap"
                          onClick={() =>
                            onStage([
                              { op: "merge", chapterId: c.id },
                              { op: "split", blockId: strandedHeading.id, title: strandedHeading.text.trim() },
                            ])
                          }
                        >
                          Start at &ldquo;{shorten(strandedHeading.text)}&rdquo; instead
                        </button>
                      )}
                    </div>

                    {strandedHeading !== undefined && !splitStaged && (
                      <p className="ab-hint ab-hint-quiet">
                        This chapter begins on something that isn&rsquo;t a heading. Its real heading is{" "}
                        {shorten(strandedHeading.text)} — usually an illustration caption left in front
                        of the title.
                      </p>
                    )}
                  </>
                )}

                {splitStaged && (
                  <div className="ab-actions">
                    <span className="ab-chip ab-chip-work">
                      <IconCheck /> A new chapter will start inside this one
                    </span>
                    <button
                      type="button"
                      className="ab-btn ab-btn-ghost ab-btn-sm"
                      onClick={() => onUnstage((o) => o.op === "split" && c.openingBlocks.some((b) => b.id === o.blockId))}
                    >
                      Undo
                    </button>
                  </div>
                )}

                {c.parts.length > 0 && (
                  <p className="ab-hint ab-hint-quiet">
                    Split into {c.parts.length + 1} parts for layout — over 60,000 characters, so the
                    reader never lays out the whole track at once.
                  </p>
                )}
              </article>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Rename({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: string;
  busy: boolean;
  onSave: (title: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const clean = draft.trim();
  return (
    <div className="ab-field" style={{ flex: "1 1 100%" }}>
      <label htmlFor="ab-rename">Chapter title</label>
      <input
        id="ab-rename"
        value={draft}
        autoFocus
        maxLength={300}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter" && clean !== "") void onSave(clean);
        }}
      />
      <div className="ab-actions">
        <button
          type="button"
          className="ab-btn ab-btn-primary ab-btn-sm"
          // An empty title is refused rather than accepted: it is a blank row in the chapter drawer
          // and a blank lock screen, and the way to lose a title is to merge the chapter away.
          disabled={busy || clean === ""}
          onClick={() => void onSave(clean)}
        >
          Save
        </button>
        <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const shorten = (s: string): string => (s.length > 28 ? `${s.slice(0, 27)}…` : s);
